import { FC } from "react";
import { Box, Button, Chip, Tooltip } from "@mui/material";
import { TFunction } from "i18next";
import FilterListIcon from "@mui/icons-material/FilterList";

import { GridLogicOperator } from "@mui/x-data-grid";

import CustomFilterForm from "./customFilterComponent";
import { InarixGridFilterModel } from "../../redux/slices/dataTable/data";
import { ColumnsForDataGridPro } from "../../redux/actions/dataTable/DataTableTypes";
import { DataTableFiltersStyles } from "../dataTable/table/MenuOptions/options/filters/FiltersSideBarStyles";
import { customFilterFormStyles } from "../dataTable/table/customFilterForm/CustomFilterFormStyles";

type CustomFilterButtonProps = {
  t: TFunction<"global", undefined>;
  isDisabled: boolean;
  loadingsValues: boolean;
  filterModel: InarixGridFilterModel;
  setShowFilterForm: (show: boolean) => void;
  showFilterForm: boolean;
  setPreselectedFilterField: (field: string | null) => void;
  preselectedField: string | null;
  columnsTable: ColumnsForDataGridPro[];
  setFilterModel: (filterModel: any) => void;
  handleApplyFilters: () => void;
  hasNoData: boolean;
  comesFromDashboard?: boolean;
  loadForFirstTime?: boolean | undefined;
  statusOptions?: Array<{
    value: string;
    label: string;
    id: number;
    description: string;
  }>;
};

export const CustomFilterButton: FC<CustomFilterButtonProps> = ({
  t,
  isDisabled,
  loadingsValues,
  filterModel,
  setShowFilterForm,
  showFilterForm,
  setPreselectedFilterField,
  preselectedField,
  columnsTable,
  setFilterModel,
  handleApplyFilters,
  hasNoData,
  comesFromDashboard,
  loadForFirstTime,
  statusOptions,
}) => {
  const handleToggleFilterForm = () => {
    setShowFilterForm(!showFilterForm);
  };

  const activeFiltersCount =
    filterModel?.items?.filter((item) => item.field && item.operator).length ||
    0;

  const activeFilter = filterModel?.items
    ?.filter((item) => item.field && item.operator)
    ?.map((item) => ({
      column: item.column,
      operator: item.operator,
      value: item.value,
    }));

  const getTooltipContent = () => {
    if (activeFiltersCount === 0) return "";

    const filterText = activeFilter
      ?.map((item) => `${item.column} ${item.operator} ${item.value}`)
      .join("\n");

    return `${t(
      "custom_filter.active_filters",
    )} (${activeFiltersCount}):\n${filterText}`;
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Tooltip
        title={getTooltipContent()}
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              whiteSpace: "pre-line",
              maxWidth: 300,
              fontSize: "0.75rem",
            },
          },
        }}
      >
        <div>
          <Button
            variant="outlined"
            onClick={handleToggleFilterForm}
            sx={{
              ...DataTableFiltersStyles.DataFiltersSideBarButton,
              ...(hasNoData &&
                !loadForFirstTime &&
                activeFiltersCount > 0 && { background: "#ff9800" }),
            }}
            disabled={isDisabled || loadingsValues}
          >
            <FilterListIcon
              fontSize="small"
              sx={customFilterFormStyles.FilterListIcon}
            />
            {t("Users_tables.menu.filters")}
          </Button>

          {activeFiltersCount > 0 && (
            <div
              style={{
                position: "absolute",
                top: -8,
                right: -5,
              }}
            >
              <Chip
                label={activeFiltersCount}
                size="small"
                sx={customFilterFormStyles.ChipFilterCount}
              />
            </div>
          )}
        </div>
      </Tooltip>

      {showFilterForm && (
        <>
          <Box
            sx={customFilterFormStyles.Box}
            onClick={() => setShowFilterForm(false)}
          />
          <CustomFilterForm
            setPreselectedFilterField={setPreselectedFilterField}
            preselectedField={preselectedField}
            columns={columnsTable}
            t={t}
            comesFromDashboard={comesFromDashboard}
            filterModel={
              filterModel || {
                items: [],
                logicOperator: GridLogicOperator.And,
              }
            }
            onFilterChange={(newFilterModel) => {
              setFilterModel(newFilterModel);
            }}
            onApplyFilters={() => {
              handleApplyFilters();
              setShowFilterForm(false);
            }}
            onClose={() => setShowFilterForm(false)}
            statusOptions={statusOptions}
          />
        </>
      )}
    </Box>
  );
};
